import TextError from "components/Form/TextError";
import { Seo } from "components/Seo";
import {ErrorMessage, Field, FieldArray, useFormikContext} from "formik";
import {Company, OpeningHoursDay} from "models/Company";
import {useEffect} from "react";

const daysOfWeek: string[] = [
  "Mandag",
  "Tirsdag",
  "Onsdag",
  "Torsdag",
  "Fredag",
  "Lørdag",
  "Søndag",
];

type DayState = "open" | "closed" | "24hours";

function OpeningHoursForm() {
  const {values, setFieldValue} = useFormikContext<Company>();

  useEffect(() => {
    populateDaysWithEmptyValues();
  }, []);

  function populateDaysWithEmptyValues() {
    const daylist = daysOfWeek.map((day) => {
      const foundDay = values.openingHours?.days?.find((d) => d.name === day);
      return foundDay || {};
    });
    setFieldValue("openingHours.days", daylist);
  }

  return (
    <> <Seo title="Åpningstider" />
      <FieldArray name="openingHours">
        {(fieldArrayProps) => {
          const {form} = fieldArrayProps;
          const {values} = form;

          function ShowOpeningHours() {
            return values.openingHours?.showOpeningHours ?? false;
          }

          function SetShowOpeningHour(show: boolean) {
            form.setFieldValue("openingHours.showOpeningHours", show);
          }

          function getDayState(day: string): DayState {
            if (values.openingHours === null) {
              return "closed";
            }
            const openingHour = values.openingHours.days.find(
              (oh: {name: string}) => oh.name === day
            );
            if (!openingHour) return "closed";
            if (openingHour.opens === "00:00" && openingHour.closes === "00:00")
              return "24hours";
            return "open";
          }

          function renderDay(
            day: string,
            index: number,
            openingHour: OpeningHoursDay
          ) {
            return (
              <tr key={day}>
                <th>{day}</th>
                <td>
                  <div className="form-check">
                    <Field
                      id={`apningstider${day}Stengt`}
                      className="form-check-input"
                      name={`openingHours.days[${index}].radio`}
                      type="radio"
                      checked={getDayState(day) === "closed"}
                      onChange={() => {
                        form.setFieldValue(`openingHours.days[${index}]`, {});
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`apningstider${day}Stengt`}
                    >
                      Stengt
                    </label>
                  </div>

                  <div className="form-check">
                    <Field
                      id={`apningstider${day}Dognåpent`}
                      className="form-check-input"
                      name={`openingHours.days[${index}].radio`}
                      type="radio"
                      checked={getDayState(day) === "24hours"}
                      onChange={() => {
                        const oph = {
                          name: day,
                          opens: "00:00",
                          closes: "00:00",
                        };
                        form.setFieldValue(`openingHours.days[${index}]`, oph);
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`apningstider${day}Dognåpent`}
                    >
                      Døgnåpent
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <Field
                      id={`apningstider${day}`}
                      className="form-check-input"
                      type="radio"
                      name={`openingHours.days[${index}].radio`}
                      checked={getDayState(day) === "open"}
                      onChange={() => {
                        const oph = {
                          name: day,
                          opens: "08:00",
                          closes: "16:00",
                        };
                        form.setFieldValue(`openingHours.days[${index}]`, oph);
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`apningstider${day}`}
                    >
                      Åpent
                    </label>
                  </div>
                  {getDayState(day) === "open" && (<>
                    <div className="d-lg-inline">
                      <label htmlFor={`${day}Fra`}>Fra</label>
                      <Field
                        id={`${day}Fra`}
                        type="time"
                        name={`openingHours.days[${index}].opens`}
                        value={
                          values.openingHours.days.find(
                            (oh: {name: string}) => oh.name === day
                          )?.opens || ""
                        }
                        // onChange={(event: { target: { value: any; }; }) => {
                        //   form.setFieldValue(
                        //     `openingHours.days[${index}].opens`,
                        //     event.target.value
                        //   );
                        // }}
                      />
                    </div>
                    <div className="d-lg-inline ms-lg-2 me-lg-2 mt-1 mb-1">
                      <label htmlFor={`${day}Til`}>Til</label>
                      <Field
                        id={`${day}Til`}
                        type="time"
                        name={`openingHours.days[${index}].closes`}
                        value={
                          values.openingHours.days.find(
                            (oh: {name: string}) => oh.name === day
                          )?.closes || ""
                        }
                        // onChange={(event: { target: { value: any; }; }) => {
                        //   form.setFieldValue(
                        //     `openingHours.days[${index}].closes`,
                        //     event.target.value
                        //   );
                        // }}
                      />
                    </div>
                  </>)}
                  <ErrorMessage name={`openingHours.days[${index}].opens`} component={TextError} />
                  <ErrorMessage name={`openingHours.days[${index}].closes`} component={TextError} />
                </td>
              </tr>
            );
          }

          return (
            <div className="cc-form">
              <div className="pb-4 border-bottom">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="openingHours.showOpeningHours"
                    id="ApningstiderOff"
                    checked={!ShowOpeningHours()}
                    onChange={() => SetShowOpeningHour(false)}
                  />
                  <label className="form-check-label" htmlFor="ApningstiderOff">
                    Ikke vis åpningstider
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="openingHours.showOpeningHours"
                    id="ApningstiderOn"
                    checked={ShowOpeningHours()}
                    onChange={() => SetShowOpeningHour(true)}
                  />
                  <label className="form-check-label" htmlFor="ApningstiderOn">
                    Vis åpningstider
                  </label>
                </div>
              </div>

              {ShowOpeningHours() && (
                <div>
                  <table className="table cc-opening-hours cc-table-2-col">
                    <tbody>
                      {daysOfWeek.map((day, index) => {
                        const openingHour = values.openingHours.days.find(
                          (d: OpeningHoursDay) => d.name === day
                        );
                        return renderDay(
                          day,
                          index,
                          openingHour || {name: day}
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        }}
      </FieldArray>
    </>
  );
}

export default OpeningHoursForm;
